import { SLEEK_TOPBAR } from "pages/DesignCanvasAgentPage/components/sleek";
import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useFloating, offset, flip, shift, autoUpdate } from "@floating-ui/react";
import { useTranslation } from "react-i18next";
import { Bell } from "lucide-react";
import { cn } from "utils/cn";
import { useAppSelector } from "store";
import { selectNotificationUnreadCount } from "store/notificationsSlice";
import NotificationsList from "./NotificationsList";

// Keep in sync with the What's New / avatar dropdown offsets so all top-bar panels hang at the same height.
const PANEL_OFFSET = 12;

// Standalone notifications bell (REF-6263 split): the announcements feed moved out to its own
// "What's New" button next to Ask Agent, so this square button now shows notifications only.
const NotificationsMenu = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const unreadCount = useAppSelector(selectNotificationUnreadCount);

  const { refs, floatingStyles } = useFloating({
    placement: "bottom-end",
    middleware: [offset(PANEL_OFFSET), flip(), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate,
  });

  // Close on outside click (the panel is portaled, so include it as "inside").
  useEffect(() => {
    if (!open) return;

    const handleMouseDown = (e: MouseEvent) => {
      const target = e.target as Node;
      const inTrigger = refs.reference.current instanceof HTMLElement && refs.reference.current.contains(target);
      const inPopover = refs.floating.current instanceof HTMLElement && refs.floating.current.contains(target);
      if (!inTrigger && !inPopover) setOpen(false);
    };

    document.addEventListener("mousedown", handleMouseDown);

    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, [open, refs.reference, refs.floating]);

  return (
    <div className="relative">
      <button
        ref={refs.setReference}
        onClick={() => setOpen((prev) => !prev)}
        className={cn("group relative flex-center h-9 w-9 rounded-full text-trueBlack transition-[filter] hover:brightness-[0.97] dark:text-white", SLEEK_TOPBAR, open && "brightness-[0.94]")}
        title={t("common:whats_new.tabs.notifications")}
        aria-label={t("common:whats_new.tabs.notifications")}
        aria-expanded={open}
      >
        <Bell className="h-[18px] w-[18px]" strokeWidth={1.75} />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 flex-center h-4 min-w-4 rounded-full bg-[#FF3B30] px-[3px] text-[10px] font-semibold text-trueWhite ring-2 ring-trueWhite dark:ring-black group-hover:ring-black-200">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {open &&
        createPortal(
          <div
            ref={refs.setFloating}
            style={{ ...floatingStyles, zIndex: 999999 }}
            className="flex flex-col w-[380px] max-h-[75vh] rounded-xl bg-trueWhite dark:bg-black border border-black-300 shadow-xl overflow-hidden"
          >
            <div className="border-b border-black-300 px-4 py-3 text-[14px] font-semibold text-trueBlack dark:text-white">{t("common:whats_new.tabs.notifications")}</div>
            <NotificationsList isActive={open} onClose={() => setOpen(false)} />
          </div>,
          document.body
        )}
    </div>
  );
};

export default NotificationsMenu;
